import * as React from 'react';
import { useSelector } from 'react-redux';
import { Alert, Select } from 'antd';
import { fetchItemsByKeyword } from '../../../actions/items/application/fetch';
import { withErrorBoundary } from '../../global/ErrorBoundary';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { createBrandTitle, createShortBrandTitle, fillInPlaceholders } from '../../../utils/String';
import { typingDone } from '../../../utils/Utils';
import constants from '../../../constants/ApplicationTranslation.json';
import { Item } from '../../../../types/item';
import { useAppDispatch } from '../../../store/ReduxStore';

type ApplicationLinksProps = {
  crossLinkBrandsEnabled: boolean;
  externalBrandsEnabled: boolean;
  parentApplicationItemIds: number[];
  selectParentItem: (id: number) => void;
  deselectParentItem: (id: number) => void;
};

const ApplicationLinks: React.FC<ApplicationLinksProps> = props => {
  const dispatch: AsyncDispatch = useAppDispatch();

  const applicationParentItems = useSelector(
    (state: ApplicationState) => state.items.application.applicationParentItems as Item[]
  );
  const brandId = useSelector((state: ApplicationState) => state.parent.brands.selectedBrandId);

  const loadItemsByKeyword = (keyword?: string) =>
    dispatch(
      fetchItemsByKeyword({
        brandId: props.crossLinkBrandsEnabled ? null : brandId,
        selectedIds: props.parentApplicationItemIds,
        inheritExternalBrands: props.externalBrandsEnabled,
        keyword,
      })
    );

  const handleSelect = (itemId: number) => {
    props.selectParentItem(itemId);
  };

  const handleDeselect = (itemId: number) => {
    props.deselectParentItem(itemId);
  };

  const handleSearch = (search: string) => typingDone(() => loadItemsByKeyword(search));

  return (
    <div className="application__links">
      <Alert
        message={fillInPlaceholders(constants.parentLinks.title, {
          itemCount: props.parentApplicationItemIds.length.toString(),
        })}
        description={
          <Select
            value={applicationParentItems
              .filter(item => props.parentApplicationItemIds.includes(item.id))
              .map(item => item.id)}
            mode="multiple"
            placeholder={constants.parentLinks.placeholder}
            filterOption={false}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            onSearch={handleSearch}
            style={{ width: '100%' }}
            options={[
              ...(applicationParentItems &&
                applicationParentItems.map(item => ({
                  key: item.id,
                  value: item.id,
                  title: `${
                    props.crossLinkBrandsEnabled
                      ? `${createBrandTitle(
                          item.brand_name!,
                          item.brand_code,
                          item.default_whitelabel_brand_code!
                        )}: `
                      : ''
                  }${item.part_number} ${item.short_name ? `(${item.short_name})` : ''}`,
                  label: `${
                    props.crossLinkBrandsEnabled
                      ? `${createShortBrandTitle(
                          item.brand_name!,
                          item.brand_code,
                          item.default_whitelabel_brand_code!
                        )}: `
                      : ''
                  }${item.part_number} ${item.short_name ? `(${item.short_name})` : ''}`,
                }))),
            ]}
          />
        }
        type="info"
        showIcon
      />
    </div>
  );
};

export { ApplicationLinks };

export default withErrorBoundary(ApplicationLinks);
